import React, { useState, useEffect } from 'react';
import {
  Input,
  Button,
  Space,
  message,
  DatePicker,
  Modal,
  Tag,
  Upload,
  Select,
  Checkbox,
  Typography,
} from 'antd';
import type { UploadFile } from 'antd';
import {
  BellOutlined,
  SendOutlined,
  PaperClipOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { createFollowUp } from '../../services/followUp';
import { updateLead } from '../../services/lead';
import { uploadAttachment } from '../../services/attachment';
import { URGENCY_LEVEL_OPTIONS } from '../../constants/leadConstants';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Text } = Typography;

// 跟进意向选项配置
const FOLLOW_STATUS_OPTIONS = [
  { value: '未联系', color: '#d9d9d9', icon: '📋' },
  { value: '未接通', color: '#faad14', icon: '📞' },
  { value: '不需要', color: '#ff4d4f', icon: '🚫' },
  { value: '无人接听', color: '#faad14', icon: '📵' },
  { value: '无效', color: '#ff4d4f', icon: '❌' },
  { value: '空号', color: '#ff4d4f', icon: '📵' },
  { value: '停机', color: '#ff4d4f', icon: '🔇' },
  { value: '稍后联系', color: '#1890ff', icon: '⏰' },
  { value: '重复', color: '#ff7a45', icon: '🔄' },
  { value: '客诉', color: '#f5222d', icon: '⚠️' },
  { value: '已加微信', color: '#52c41a', icon: '💬' },
  { value: '已加企微', color: '#13c2c2', icon: '👥' },
  { value: '已付咨询费', color: '#52c41a', icon: '💰' },
  { value: '邀约到所', color: '#722ed1', icon: '🏢' },
  { value: '已成交', color: '#389e0d', icon: '✅' },
];

interface QuickFollowUpProps {
  leadId: number;
  lead?: any;
  onSuccess?: () => void;
  onLeadUpdate?: (lead: any) => void;
  onBeforeClose?: () => boolean; // 返回 true 表示可以关闭，false 表示需要确认
}

const STORAGE_KEY_PREFIX = 'quick_follow_up_';

const QuickFollowUp: React.FC<QuickFollowUpProps> = ({ 
  leadId, 
  lead,
  onSuccess,
  onLeadUpdate,
  onBeforeClose
}) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [reminderVisible, setReminderVisible] = useState(false);
  const [reminderTime, setReminderTime] = useState<dayjs.Dayjs | null>(null);
  const [followStatus, setFollowStatus] = useState<string>(lead?.follow_status || '未接通');
  const [urgencyLevel, setUrgencyLevel] = useState<string>(lead?.urgency_level || 'D类');
  const [attachments, setAttachments] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [returnToPool, setReturnToPool] = useState(false);
  const [hasCache, setHasCache] = useState(false);

  const storageKey = `${STORAGE_KEY_PREFIX}${leadId}`;

  // 加载缓存数据（只在组件挂载时执行一次）
  useEffect(() => {
    const savedData = localStorage.getItem(storageKey);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        const hasContent = (parsed.content && parsed.content.trim().length > 0) || 
                          (parsed.attachments && parsed.attachments.length > 0);
        
        if (hasContent) {
          setContent(parsed.content || '');
          setFollowStatus(parsed.followStatus || lead?.follow_status || '未接通');
          setUrgencyLevel(parsed.urgencyLevel || lead?.urgency_level || 'D类');
          setReminderTime(parsed.reminderTime ? dayjs(parsed.reminderTime) : null);
          setAttachments(parsed.attachments || []);
          setReturnToPool(parsed.returnToPool || false);
          setHasCache(true);
        }
      } catch (error) {
        console.error('Failed to parse saved follow-up data:', error);
      }
    }
  }, [leadId]); // 只依赖 leadId，避免重复触发

  // 保存到缓存
  const saveToCache = () => {
    const data = {
      content,
      followStatus,
      urgencyLevel,
      reminderTime: reminderTime ? reminderTime.toISOString() : null,
      attachments,
      returnToPool,
    };
    localStorage.setItem(storageKey, JSON.stringify(data));
  };

  // 清除缓存
  const clearCache = () => {
    localStorage.removeItem(storageKey);
    setHasCache(false);
  };

  // 检查是否有未提交的内容
  const hasUnsavedContent = () => {
    return content.trim().length > 0 || attachments.length > 0;
  };

  // 监听内容变化，自动保存（但不改变 hasCache 状态）
  useEffect(() => {
    if (hasUnsavedContent()) {
      saveToCache();
    }
  }, [content, followStatus, urgencyLevel, reminderTime, attachments, returnToPool]);

  // Handle file upload
  const handleFileUpload = async (file: File): Promise<boolean> => {
    // Check file size (50MB limit)
    if (file.size > 50 * 1024 * 1024) {
      message.error('文件大小不能超过50MB');
      return false;
    }

    // Check max count
    if (attachments.length >= 5) {
      message.error('最多只能上传5个附件');
      return false;
    }

    setUploading(true);
    try {
      const attachment = await uploadAttachment(file, 'lead', leadId);
      setAttachments([...attachments, attachment]);
      message.success('附件上传成功');
    } catch (error: any) {
      console.error('Upload failed:', error);
      message.error('上传失败：' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }

    return false; // Prevent default upload behavior
  };

  // Handle remove attachment
  const handleRemoveAttachment = (attachmentId: number) => {
    setAttachments(attachments.filter(att => att.id !== attachmentId));
  };

  // Handle submit follow-up
  const handleSubmit = async () => {
    if (!content.trim()) {
      message.warning('请输入跟进内容');
      return;
    }

    setLoading(true);
    try {
      // 构建跟进内容（包含跟进意向变化和提醒信息）
      let fullContent = content.trim();
      
      // 如果跟进意向有变化，在内容中体现
      if (followStatus !== lead?.follow_status) {
        const oldStatus = lead?.follow_status || '未设置';
        fullContent = `${fullContent}\n\n🔄 跟进意向：${oldStatus} → ${followStatus}`;
      }
      
      // 添加提醒信息
      if (reminderTime) {
        fullContent += `\n📅 下次跟进时间：${reminderTime.format('YYYY-MM-DD HH:mm')}`;
      }

      // 添加附件信息
      if (attachments.length > 0) {
        fullContent += `\n📎 附件 (${attachments.length}个)：\n`;
        attachments.forEach(att => {
          // 优先使用filename，其次file_name，最后从file_path提取
          const fileName = att.filename || att.file_name || att.file_path?.split('/').pop() || '附件';
          fullContent += `  • ${fileName}\n`;
        });
      }

      // 1. 先创建用户跟进记录（包含附件信息）
      await createFollowUp({
        entity_type: 'lead',  // 使用 entity_type 字段
        entity_id: leadId,
        content: fullContent,
        follow_type: 'user',  // 统一使用 user 类型
        next_follow_up_time: reminderTime ? reminderTime.format('YYYY-MM-DD HH:mm:ss') : undefined,
        reminder_enabled: reminderTime ? true : false,
        attachments: attachments.map(att => {
          // 优先使用filename，其次file_name，最后从file_path提取
          const fileName = att.filename || att.file_name || att.file_path?.split('/').pop() || '附件';
          return {
            id: att.id,
            file_name: fileName,
            file_path: att.file_path,
            file_size: att.file_size,
          };
        }),
      });

      // 2. 再静默更新线索（不创建系统记录）
      const updateData: any = { 
        follow_status: followStatus,
        urgency_level: urgencyLevel,
        next_follow_time: reminderTime ? reminderTime.format('YYYY-MM-DD HH:mm:ss') : undefined,
        reminder_enabled: reminderTime ? 1 : 0,
      };
      
      // 如果勾选了退回公海池，则更新状态和清空负责人
      if (returnToPool) {
        updateData.status = 'public_pool';
        updateData.assigned_to = null;
      }
      
      await updateLead(leadId, updateData, true); // 传递 skipSystemRecord=true

      message.success(returnToPool ? '跟进记录已发布，线索已退回公海池' : '跟进记录已发布');
      
      // 清除缓存
      clearCache();
      
      // 重置表单
      setContent('');
      setReminderTime(null);
      setAttachments([]);
      setReturnToPool(false);
      
      // 通知父组件更新
      if (onLeadUpdate && lead) {
        onLeadUpdate({ 
          ...lead, 
          follow_status: followStatus,
          urgency_level: urgencyLevel,
          next_follow_time: reminderTime ? reminderTime.format('YYYY-MM-DD HH:mm:ss') : undefined,
          status: returnToPool ? 'public_pool' : lead.status,
          assigned_to: returnToPool ? null : lead.assigned_to,
        });
      }
      
      onSuccess?.();
    } catch (error) {
      message.error('发布跟进记录失败');
    } finally {
      setLoading(false);
    }
  };

  // 手动清除缓存
  const handleClearCache = () => {
    clearCache();
    setContent('');
    setReminderTime(null);
    setAttachments([]);
    setReturnToPool(false);
    setFollowStatus(lead?.follow_status || '未接通');
    setUrgencyLevel(lead?.urgency_level || 'D类');
    message.success('已清除缓存数据');
  };

  // 获取当前跟进意向的配置
  const currentStatusOption = FOLLOW_STATUS_OPTIONS.find(opt => opt.value === followStatus);

  return (
    <div className="quick-follow-up">
      {hasCache && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#e6f7ff', borderRadius: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: '13px' }}>
            检测到未提交的跟进内容，已自动恢复
          </Text>
          <Button size="small" type="link" onClick={handleClearCache}>
            清除缓存
          </Button>
        </div>
      )}
      <div className="quick-follow-up-input">
        <TextArea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="点击输入跟进内容..."
          autoSize={{ minRows: 3, maxRows: 6 }}
          style={{ marginBottom: 12 }}
        />
        
        {/* 显示已选择的提醒和附件 */}
        {(reminderTime || attachments.length > 0) && (
          <Space wrap style={{ marginBottom: 12 }}>
            {reminderTime && (
              <Tag 
                closable 
                onClose={() => setReminderTime(null)}
                icon={<BellOutlined />}
                color="blue"
              >
                提醒: {reminderTime.format('MM-DD HH:mm')}
              </Tag>
            )}
            {attachments.map((att) => (
              <Tag 
                key={att.id}
                closable 
                onClose={() => handleRemoveAttachment(att.id)}
                icon={<PaperClipOutlined />}
                color="green"
              >
                {att.file_name}
              </Tag>
            ))}
          </Space>
        )}

        <div className="quick-follow-up-actions">
          <Space size="middle">
            <Upload
              beforeUpload={handleFileUpload}
              showUploadList={false}
              disabled={uploading || attachments.length >= 5}
            >
              <Button
                type="text"
                icon={<PaperClipOutlined />}
                size="small"
                loading={uploading}
                disabled={attachments.length >= 5}
              >
                附件
              </Button>
            </Upload>
            <Button
              type="text"
              icon={<BellOutlined />}
              size="small"
              onClick={() => setReminderVisible(true)}
            >
              提醒
            </Button>
            {/* 跟进意向选择器 */}
            <Select
              value={followStatus}
              onChange={setFollowStatus}
              style={{ width: 140 }}
              size="small"
              placeholder="跟进意向"
            >
              {FOLLOW_STATUS_OPTIONS.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  <span style={{ marginRight: 4 }}>{option.icon}</span>
                  {option.value}
                </Select.Option>
              ))}
            </Select>
            {/* 紧急程度选择器 */}
            <Select
              value={urgencyLevel}
              onChange={setUrgencyLevel}
              style={{ width: 100 }}
              size="small"
              placeholder="紧急程度"
            >
              {URGENCY_LEVEL_OPTIONS.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
            {/* 退回公海池勾选框 */}
            <Checkbox
              checked={returnToPool}
              onChange={(e) => setReturnToPool(e.target.checked)}
            >
              退回公海池
            </Checkbox>
          </Space>
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={loading}
            onClick={handleSubmit}
          >
            发布跟进
          </Button>
        </div>
      </div>

      {/* Reminder Modal */}
      <Modal
        title="设置提醒"
        open={reminderVisible}
        onOk={() => {
          if (!reminderTime) {
            message.warning('请选择提醒时间');
            return;
          }
          setReminderVisible(false);
        }}
        onCancel={() => {
          setReminderVisible(false);
        }}
        okText="确定"
        cancelText="取消"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>选择提醒时间：</div>
          <DatePicker
            showTime
            value={reminderTime}
            onChange={setReminderTime}
            style={{ width: '100%' }}
            placeholder="选择日期和时间"
            disabledDate={(current) => current && current < dayjs().startOf('day')}
          />
        </Space>
      </Modal>
    </div>
  );
};

export default QuickFollowUp;
